
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { autoApproveTutor } from '@/utils/autoApproveTutor';
import { toast } from '@/hooks/use-toast';

interface SimpleTutorProfile {
  id: string;
  user: string;
  [key: string]: any;
}

export const useTutorStatus = () => {
  const { user } = useAuth();
  const [tutorProfile, setTutorProfile] = useState<SimpleTutorProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTutorProfile = async () => {
      if (!user || user.role !== 'tutor') {
        setIsLoading(false);
        return;
      }

      try {
        const profile = await pb.collection('tutors').getFirstListItem(
          `user = "${user.id}"`,
          {
            requestKey: `tutor_status_${user.id}_${Date.now()}`,
          }
        );
        // Convert RecordModel to our interface safely
        setTutorProfile({
          id: profile.id,
          user: profile.user,
          ...profile
        });
      } catch (error: any) {
        // Handle 404 error specifically - this is normal for new tutors
        if (error?.status === 404) {
          setTutorProfile(null);
        } else {
          console.error('Error fetching tutor profile:', error);
          setTutorProfile(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTutorProfile();
  }, [user]);


  // Check auto-approval setting and handle pending tutors
  // Auto-approval effect - automatically approve pending tutors if admin settings allow
  // Removed auto-approval from page load - it causes authentication timing issues
  // Auto-approval should be handled by admin panel, not during user page load

  // Use the status from the user record, not the tutor profile
  const userStatus = user?.status as 'active' | 'pending' | 'suspended';
  
  const isPendingTutor = userStatus === 'pending';
  const isActiveTutor = userStatus === 'active';
  const isSuspendedTutor = userStatus === 'suspended';

  const hasAccessToTutorFeatures = () => {
    if (!user) {
      return false;
    }
    
    if (user.role !== 'tutor') {
      return false;
    }
    
    const hasAccess = userStatus === 'active';
    return hasAccess;
  };

  return {
    tutorProfile,
    isPendingTutor,
    isActiveTutor,
    isSuspendedTutor,
    hasAccessToTutorFeatures,
    isLoading
  };
};
